import { useMutation } from '@tanstack/vue-query';
import http from '@/services/http';

export interface RawDdexPartyRef {
  id: number;
  party_id: string;
  name: string | null;
}

export interface RawDdexExclusionUserRef {
  id: number;
  name: string | null;
  email: string | null;
}

export interface RawDdexPartyExclusion {
  id: number;
  ddex_configuration_id: number | null;
  raw_ddex_party_sender_id: number | null;
  raw_ddex_party_sent_on_behalf_of_id: number | null;
  reason: string | null;
  created_by_user_id: number | null;
  tracks_purge_queued_at: string | null;
  tracks_purged_at: string | null;
  sender: RawDdexPartyRef | null;
  sent_on_behalf_of: RawDdexPartyRef | null;
  created_by_user: RawDdexExclusionUserRef | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface RawDdexPartyExclusionInput {
  ddex_configuration_id: number;
  raw_ddex_party_sent_on_behalf_of_id: number;
  reason?: string | null;
}

const RESOURCE_BASE = '/api/raw_ddex_party_exclusion';

/** Stop polling when a queued purge has not completed within this window. */
export const PURGE_POLL_STALE_MS = 10 * 60 * 1000;

interface ItemEnvelope<T> {
  data: T;
}

interface PurgeTracksResponse {
  message: string;
  raw_ddex_party_exclusion_id: number;
}

function purgeQueuedAgeMs(exclusion: RawDdexPartyExclusion): number | null {
  if (exclusion.tracks_purge_queued_at == null) {
    return null;
  }

  return Date.now() - new Date(exclusion.tracks_purge_queued_at).getTime();
}

// Exclusions are surfaced through DdexConfiguration.exclusions, so callers invalidate the configs
// query (DDEX_CONFIGURATIONS_QUERY_KEY) after these mutations. The mutations don't invalidate it
// here to avoid a value-level import cycle with useDdexConfigurations (which imports the purge-status
// helpers below for its poll gate).
export function useCreateRawDdexPartyExclusion() {
  return useMutation({
    mutationFn: async (input: RawDdexPartyExclusionInput) => {
      const response = await http.post<ItemEnvelope<RawDdexPartyExclusion>>(RESOURCE_BASE, input);
      return response.data?.data;
    },
  });
}

export function usePurgeRawDdexPartyExclusionTracks() {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await http.post<PurgeTracksResponse>(`${RESOURCE_BASE}/${id}/purge-tracks`);
      return response.data;
    },
  });
}

export function isCatalogPurgeComplete(exclusion: RawDdexPartyExclusion): boolean {
  // Use loose != so missing API keys (undefined) count as "not processed". Strict
  // !== null treats undefined as complete when the DB columns are not migrated yet.
  return exclusion.tracks_purged_at != null;
}

export function isCatalogPurgeStale(exclusion: RawDdexPartyExclusion): boolean {
  if (isCatalogPurgeComplete(exclusion) || exclusion.tracks_purge_queued_at == null) {
    return false;
  }

  const ageMs = purgeQueuedAgeMs(exclusion);

  return ageMs != null && ageMs >= PURGE_POLL_STALE_MS;
}

export function isCatalogPurgePending(exclusion: RawDdexPartyExclusion): boolean {
  return (
    exclusion.tracks_purge_queued_at != null
    && exclusion.tracks_purged_at == null
    && !isCatalogPurgeStale(exclusion)
  );
}

export function canPurgeCatalogTracks(exclusion: RawDdexPartyExclusion): boolean {
  return exclusion.tracks_purged_at == null && exclusion.tracks_purge_queued_at == null;
}
